import { Link } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';

export type KpiTone = 'pitch' | 'navy' | 'gold';

const TONE: Record<KpiTone, { icon: string; hover: string; hint: string }> = {
  pitch: { icon: 'bg-pitch-50 text-pitch', hover: 'hover:border-pitch', hint: 'text-pitch' },
  navy: { icon: 'bg-navy-50 text-navy', hover: 'hover:border-navy', hint: 'text-navy' },
  gold: { icon: 'bg-gold-50 text-gold-700', hover: 'hover:border-gold', hint: 'text-gold-700' },
};

export interface KpiCardProps {
  label: string;
  /** Pre-formatted — a KPI never formats its own number, see `formatCount`. */
  value: string;
  icon: LucideIcon;
  tone?: KpiTone;
  /** The line under the number: what it is out of, or what it is waiting on. */
  hint?: ReactNode;
  /** Draws the hint in the tone's colour, for a number that is work waiting. */
  emphasizeHint?: boolean;
  /** The screen the number came from. */
  href: string;
}

/**
 * The dashboard KPI tile: the label beside its icon, the number, a hint under it.
 *
 * Every tile is a link to the list behind the number — "٤ طلبات توثيق معلّقة" goes to the
 * verification queue. That is the whole point of the dashboard for a two-person admin
 * team, so the tile is a real `<Link>` rather than a card with a click handler.
 */
export function KpiCard({
  label,
  value,
  icon: Icon,
  tone = 'pitch',
  hint,
  emphasizeHint = false,
  href,
}: KpiCardProps) {
  return (
    <Card asChild>
      <Link
        data-slot="kpi-card"
        to={href}
        className={cn('group block p-5 transition hover:shadow-md', TONE[tone].hover)}
      >
        <div className="flex items-center justify-between">
          <span className="text-[12.5px] font-semibold text-conc-500">{label}</span>
          <span className={cn('grid h-10 w-10 place-items-center rounded-xl', TONE[tone].icon)}>
            <Icon aria-hidden className="h-5 w-5" />
          </span>
        </div>
        <p className="num mt-2 text-2xl font-bold text-navy">{value}</p>
        {hint !== undefined && (
          <div className="mt-3 text-[11.5px]">
            <span className={emphasizeHint ? cn('font-bold', TONE[tone].hint) : 'text-conc-400'}>
              {hint}
            </span>
          </div>
        )}
      </Link>
    </Card>
  );
}
